// Radha — invoice_email payment tier (059): send an invoice by email
// (send-invoice-email, Resend-backed, same shape as send-digest-email) and
// read back the delivery history (005's delivery_attempts, now readable —
// see 060) to show "last emailed" without re-sending blind.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "../lib/supabase";
import { edgeFunctionErrorMessage } from "../lib/edgeFunctionError";

export interface DeliveryAttempt {
  id: string; invoice_id: string; channel: string; recipient: string | null;
  status: "queued" | "sent" | "delivered" | "bounced" | "failed";
  sent_at: string | null; error_detail: string | null;
}

export function useInvoiceEmailAttempts() {
  return useQuery({
    queryKey: ["invoice-delivery-attempts"],
    queryFn: async (): Promise<DeliveryAttempt[]> => {
      const { data, error } = await supabase
        .from("delivery_attempts")
        .select("id, invoice_id, channel, recipient, status, sent_at, error_detail")
        .eq("channel", "email")
        .order("sent_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 10_000,
  });
}

export function useSendInvoiceEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ invoiceId, recipientEmail }: { invoiceId: string; recipientEmail: string }) => {
      const { data, error } = await supabase.functions.invoke("send-invoice-email", {
        body: { invoice_id: invoiceId, recipient_email: recipientEmail },
      });
      if (error) throw new Error(await edgeFunctionErrorMessage(error));
      if (data?.error) throw new Error(data.error);
      return data as DeliveryAttempt;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["invoice-delivery-attempts"] }),
  });
}

export function invoiceEmailErrorMessage(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  const m = msg.match(/^([A-Z_]+):\s*(.*)$/);
  return m ? m[2] || m[1] : msg;
}
